import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithReauth } from "@src/utils/baseQuery";
import { APIResponse } from "./CommonApiSlice";
import {
  GetDashboardBatchContent,
  GetDashboardContent,
  GetDropdownBatchContent,
  UserData,
} from "@src/pages/dashboard/dashboard-base-component/DashboardTypes";

export interface GetDashboardResponse extends APIResponse {
  response: GetDashboardContent;
}

export interface GetDashboardBatchResponse extends APIResponse {
  response: GetDashboardBatchContent;
}

export interface GetDropdownBatchResponse extends APIResponse {
  response: GetDropdownBatchContent[];
}

export interface GetUserDataResponse extends APIResponse {
  response: UserData;
}

export type UpdateProfilePhotoResponse = APIResponse;
export type RemoveProfilePhotoResponse = APIResponse;

export const DashboardApiSlice = createApi({
  reducerPath: "DashboardApi",
  baseQuery: baseQueryWithReauth,
  endpoints: (builder) => ({
    getDashboard: builder.query<
      GetDashboardResponse,
      {
        organizationId: string;
      }
    >({
      query: ({ organizationId }) => ({
        url: `student/dashboard?organizationId=${organizationId}`,
        method: "GET",
      }),
    }),
    getDashboardBatch: builder.query<
      GetDashboardBatchResponse,
      {
        batchCode: string;
      }
    >({
      query: ({ batchCode }) => ({
        url: `student/dashboard/batch?batchCode=${batchCode}`,
        method: "GET",
      }),
    }),
    getDropdownBatch: builder.query<
      GetDropdownBatchResponse,
      {
        parentId: string;
        subjectId?: string;
      }
    >({
      query: ({ parentId, subjectId }) => ({
        url: `batch/get-batch-dropdown?parentId=${parentId}${subjectId ? `&subjectId=${subjectId}` : ""}`,
        method: "GET",
      }),
    }),
    updateProfilePhoto: builder.mutation<UpdateProfilePhotoResponse, FormData>({
      query: (formData) => ({
        url: "user/upload-profile-photo",
        method: "PUT",
        body: formData,
      }),
    }),
    getUserData: builder.query<GetUserDataResponse, void>({
      query: () => ({
        url: `user/get-user-data`,
        method: "GET",
      }),
    }),
    removeProfilePhoto: builder.mutation<RemoveProfilePhotoResponse, void>({
      query: () => ({
        url: "user/remove-profile-photo",
        method: "PATCH",
      }),
    }),
  }),
});

export const {
  useGetDashboardQuery,
  useGetDashboardBatchQuery,
  useUpdateProfilePhotoMutation,
  useGetUserDataQuery,
  useRemoveProfilePhotoMutation,
  useGetDropdownBatchQuery,
  useLazyGetDropdownBatchQuery,
} = DashboardApiSlice;
